// Phone remote control: the server forwards modules' ctx.tv.call('frame.remote.*') calls
// here over the Edge channel (see modules/remote). Returns a snapshot of the launcher
// screen (tiles, focus, mode, volume) and applies key presses and tile selection.
//
// Keys: when the launcher is in front, a synthetic keydown event (nav.ts and gallery wake-up
// work as usual). When another app is in front, Samsung's local remote-control service
// (127.0.0.1:8001/8002, samsung.remote.control) is tried; the TV may ask for permission the first time.
import { focus, focusFirst } from './nav.ts';
import type { Runtime } from './runtime.ts';

declare const tizen: any;

export interface RemoteItem {
  /** '<widgetId>:<index>', or 'o:<index>' in an open full-screen layer. */
  id: string;
  label: string;
  sub?: string;
  /** Image URL (server-relative '/…' or absolute). */
  icon?: string;
  /** SVG of a built-in icon (currentColor). */
  svg?: string;
  color?: string;
  active?: boolean;
  focused?: boolean;
}

export interface RemoteGroup {
  title?: string;
  items: RemoteItem[];
}

export interface RemoteSnapshot {
  /** 'idle' = gallery, 'active' = launcher. */
  mode: 'idle' | 'active';
  /** Whether the launcher is on screen (false when another app is in front). */
  visible: boolean;
  /** The most recently opened app while the launcher is in the background. */
  app?: { id: string; name: string };
  /** The open full-screen UI (Settings, a game…). */
  overlay?: { title?: string; items: RemoteItem[] };
  groups: RemoteGroup[];
  volume?: number;
  muted?: boolean;
  at: number;
}

type Key = 'up' | 'down' | 'left' | 'right' | 'ok' | 'back' | 'home' | 'gallery';

// keyCode (inside the launcher) and the Samsung remote-control service key name.
const KEYS: Record<string, [number, string]> = {
  up: [38, 'KEY_UP'],
  down: [40, 'KEY_DOWN'],
  left: [37, 'KEY_LEFT'],
  right: [39, 'KEY_RIGHT'],
  ok: [13, 'KEY_ENTER'],
  back: [10009, 'KEY_RETURN'],
};

const clean = (s: string | null | undefined, max = 60) => (s || '').replace(/\s+/g, ' ').trim().slice(0, max);
const wait = (ms: number) => new Promise((r) => setTimeout(r, ms));

export class Remote {
  private rt: Runtime;
  /** The most recently opened app (from widgets or the server). */
  lastLaunch = { id: '', label: '', at: 0 };
  private samsung?: Promise<WebSocket>;

  constructor(rt: Runtime) {
    this.rt = rt;
  }

  /** Called on runtime.tv and Edge 'tizen.application.launch' calls. */
  noteLaunch(appId: unknown) {
    this.lastLaunch = { id: String(appId || ''), label: '', at: Date.now() };
  }

  async call(api: string, args: unknown[]): Promise<unknown> {
    switch (api) {
      case 'frame.remote.snapshot':
        return this.snapshot(!!args[0]);
      case 'frame.remote.key':
        await this.key(String(args[0]) as Key);
        await wait(120);
        return this.snapshot(!!args[1]);
      case 'frame.remote.press':
        await this.press(String(args[0] || ''), args[1] == null ? undefined : String(args[1]));
        await wait(250); // let the opened layer render
        return this.snapshot(!!args[2]);
      default:
        throw new Error(`Unknown operation: ${api}`);
    }
  }

  // ---------- snapshot ----------

  private visible() {
    return document.visibilityState === 'visible';
  }

  private label(el: Element) {
    const t = el.querySelector('.fl-tile-label');
    if (t) return clean(t.textContent);
    return clean(el.getAttribute('aria-label') || el.getAttribute('title') || (el as HTMLElement).innerText || el.textContent) || '•';
  }

  private item(el: Element, id: string, icons: boolean): RemoteItem {
    const it: RemoteItem = { id, label: this.label(el) };
    const sub = el.querySelector('.fl-tile-sub');
    if (sub) it.sub = clean(sub.textContent, 80);
    const color = (el as HTMLElement).style?.getPropertyValue('--tile').trim();
    if (color) it.color = color;
    if (el.classList.contains('is-active')) it.active = true;
    if (el === document.activeElement && this.rt.mode() === 'active' && this.visible()) it.focused = true;
    if (icons) {
      const img = el.querySelector('img.fl-tile-img') as HTMLImageElement | null;
      const src = img?.src || '';
      if (/^https?:/.test(src)) it.icon = src.startsWith(this.rt.baseUrl) ? `/${src.slice(this.rt.baseUrl.length)}` : src;
      const svg = el.querySelector('svg.fl-tile-svg');
      if (!it.icon && svg && svg.outerHTML.length < 4000) it.svg = svg.outerHTML;
    }
    return it;
  }

  /** A widget's focusable elements outside its full-screen layers. */
  private widgetFocusables(w: Element) {
    return Array.from(w.querySelectorAll('[data-focus]')).filter((el) => !el.closest('[data-focus-scope]'));
  }

  private overlayScope() {
    const scopes = document.querySelectorAll('[data-focus-scope]');
    return scopes.length ? scopes[scopes.length - 1] : null;
  }

  snapshot(icons = false): RemoteSnapshot {
    const groups: RemoteGroup[] = [];
    const widgets = document.querySelectorAll('.fl-zone-launcher > .fl-widget[data-widget]');
    widgets.forEach((w) => {
      const els = this.widgetFocusables(w);
      if (!els.length) return;
      const id = w.getAttribute('data-widget')!;
      const items = els.map((el, i) => this.item(el, `${id}:${i}`, icons));
      const title = clean(w.querySelector('.fl-row-title')?.textContent) || undefined;
      // Untitled single-tile widgets sit side by side on the TV; group them on the phone too.
      const prev = groups[groups.length - 1];
      if (!title && prev && !prev.title) prev.items.push(...items);
      else groups.push({ title, items });
    });

    const snap: RemoteSnapshot = { mode: this.rt.mode(), visible: this.visible(), groups, at: Date.now() };

    const scope = this.overlayScope();
    if (scope) {
      const items = Array.from(scope.querySelectorAll('[data-focus]'))
        // With nested layers, only the topmost layer's elements.
        .filter((el) => el.closest('[data-focus-scope]') === scope)
        .map((el, i) => this.item(el, `o:${i}`, icons));
      this.disambiguate(items, scope);
      snap.overlay = { title: clean(scope.querySelector('.fl-fullscreen-title')?.textContent) || undefined, items };
    }

    if (!snap.visible && this.lastLaunch.id) {
      let name = this.lastLaunch.label;
      if (!name && this.rt.isTV) {
        try {
          name = tizen.application.getAppInfo(this.lastLaunch.id).name;
        } catch {}
      }
      snap.app = { id: this.lastLaunch.id, name: name || this.lastLaunch.id };
    }

    if (this.rt.isTV) {
      try {
        snap.volume = tizen.tvaudiocontrol.getVolume();
        snap.muted = tizen.tvaudiocontrol.isMute();
      } catch {}
    }
    return snap;
  }

  /** Give same-named tiles (e.g. each phone's "Remove") the text of the row they're in. */
  private disambiguate(items: RemoteItem[], scope: Element) {
    const els = Array.from(scope.querySelectorAll('[data-focus]')).filter((el) => el.closest('[data-focus-scope]') === scope);
    items.forEach((it, i) => {
      if (it.sub || items.filter((x) => x.label === it.label).length < 2) return;
      let p = els[i].parentElement;
      for (let depth = 0; p && p !== scope && depth < 3; depth++, p = p.parentElement) {
        const own = (els[i] as HTMLElement).innerText || '';
        const text = clean(((p as HTMLElement).innerText || '').replace(own, ''), 50);
        if (text) {
          it.sub = text;
          return;
        }
      }
    });
  }

  // ---------- tile selection ----------

  private resolve(id: string, label?: string): HTMLElement | null {
    const [scopeId, idx] = [id.slice(0, id.lastIndexOf(':')), Number(id.slice(id.lastIndexOf(':') + 1))];
    let list: Element[] = [];
    if (scopeId === 'o') {
      const scope = this.overlayScope();
      if (scope) list = Array.from(scope.querySelectorAll('[data-focus]')).filter((el) => el.closest('[data-focus-scope]') === scope);
    } else {
      const w = Array.from(document.querySelectorAll('.fl-zone-launcher > .fl-widget[data-widget]')).find((x) => x.getAttribute('data-widget') === scopeId);
      if (w) list = this.widgetFocusables(w);
    }
    const el = list[idx];
    // If the screen changed since the phone saw it, find by label (only on a single match; don't guess).
    if (el && (label == null || this.label(el) === label)) return el as HTMLElement;
    const same = label == null ? [] : list.filter((x) => this.label(x) === label);
    return same.length === 1 ? (same[0] as HTMLElement) : null;
  }

  async press(id: string, label?: string) {
    const el = this.resolve(id, label);
    if (!el) throw Object.assign(new Error(this.rt.t.remoteTileGone), { userError: true });
    const hidden = !this.visible();
    const before = this.lastLaunch.at;
    this.rt.wake();
    focus(el);
    el.click();
    if (this.lastLaunch.at !== before) this.lastLaunch.label = label || this.label(el);
    // If the tile didn't open an app (e.g. a full-screen UI), bring the launcher to the front.
    else if (hidden) this.rt.bringToFront();
  }

  // ---------- keys ----------

  async key(k: Key) {
    if (k === 'home') {
      if (!this.visible()) this.rt.bringToFront();
      this.rt.wake();
      setTimeout(focusFirst, 80);
      return;
    }
    if (k === 'gallery') {
      if (!this.visible()) this.rt.bringToFront();
      this.rt.sleep();
      return;
    }
    const def = KEYS[k];
    if (!def) throw Object.assign(new Error(this.rt.t.remoteUnknownKey(k)), { userError: true });
    if (this.visible() || !this.rt.isTV) return this.dispatch(def[0]);
    await this.samsungKey(def[1]);
  }

  /** Like a real key press inside the launcher: nav.ts and the gallery wake-up listeners see it. */
  private dispatch(code: number) {
    const target = document.activeElement || document.body;
    for (const type of ['keydown', 'keyup']) {
      const e = new KeyboardEvent(type, { bubbles: true, cancelable: true });
      Object.defineProperty(e, 'keyCode', { get: () => code });
      Object.defineProperty(e, 'which', { get: () => code });
      target.dispatchEvent(e);
    }
  }

  // ---------- Samsung's local remote-control service (while another app is in front) ----------

  private async samsungKey(key: string) {
    const ws = await this.samsungSocket();
    ws.send(JSON.stringify({ method: 'ms.remote.control', params: { Cmd: 'Click', DataOfCmd: key, Option: 'false', TypeOfRemote: 'SendRemoteKey' } }));
  }

  private samsungSocket(): Promise<WebSocket> {
    if (this.samsung) return this.samsung;
    const name = btoa('Frame Remote');
    const token = localStorage.getItem('fl.samsungRemoteToken');
    const urls = [
      `ws://127.0.0.1:8001/api/v2/channels/samsung.remote.control?name=${name}`,
      `wss://127.0.0.1:8002/api/v2/channels/samsung.remote.control?name=${name}${token ? `&token=${token}` : ''}`,
    ];
    const tryUrl = (url: string) =>
      new Promise<WebSocket>((res, rej) => {
        const ws = new WebSocket(url);
        // The TV asks for permission on screen on the first connection; give the user time.
        const timer = setTimeout(() => {
          ws.close();
          rej(new Error('timed out'));
        }, 15_000);
        ws.onmessage = (e) => {
          let m: { event?: string; data?: { token?: string } } = {};
          try {
            m = JSON.parse(String(e.data));
          } catch {}
          if (m.event === 'ms.channel.connect') {
            clearTimeout(timer);
            if (m.data?.token) localStorage.setItem('fl.samsungRemoteToken', m.data.token);
            res(ws);
          } else if (m.event === 'ms.channel.unauthorized' || m.event === 'ms.channel.timeOut') {
            clearTimeout(timer);
            ws.close();
            rej(new Error('the TV denied permission'));
          }
        };
        ws.onerror = () => {
          clearTimeout(timer);
          rej(new Error('could not connect'));
        };
        ws.onclose = () => {
          clearTimeout(timer);
          if (this.samsung && this.samsungWs === ws) this.samsung = undefined;
          rej(new Error('connection closed'));
        };
      });
    this.samsung = (async () => {
      let last: Error | undefined;
      for (const url of urls) {
        try {
          const ws = await tryUrl(url);
          this.samsungWs = ws;
          return ws;
        } catch (e) {
          last = e as Error;
        }
      }
      throw last;
    })().catch((e: Error) => {
      this.samsung = undefined;
      this.rt.log('warn', `Samsung remote-control service: ${e.message}`, 'remote');
      throw Object.assign(new Error(this.rt.t.remoteSamsungFailed), { userError: true });
    });
    return this.samsung;
  }

  private samsungWs?: WebSocket;
}
